import { useMutation, useQuery } from '@tanstack/react-query'
import { VideosFilterParams, CreateVideoParams, UpdateVideoParams } from '@/types/video'
import { useVideosStore } from '@/store'
import { SortOrderEnum } from '@/enums'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useVideos = (params: VideosFilterParams) => {
  const fetchVideos = useVideosStore(state => state.fetchVideos)

  return useQuery({
    queryKey: ['videos', params],
    queryFn: () => fetchVideos(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useVideo = (id: string) => {
  const getVideo = useVideosStore(state => state.getVideo)

  return useQuery({
    queryKey: ['video', id],
    queryFn: () => getVideo(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateVideo = () => {
  const createVideo = useVideosStore(state => state.createVideo)

  return useMutation({
    mutationFn: (params: CreateVideoParams) => createVideo(params),
    onSuccess: () => {}
  })
}

export const useUpdateVideo = () => {
  const updateVideo = useVideosStore(state => state.updateVideo)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateVideoParams }) => updateVideo(id, params),
    onSuccess: () => {}
  })
}

export const useSortVideo = () => {
  const sortVideo = useVideosStore(state => state.sortVideo)

  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: SortOrderEnum }) => sortVideo(id, type),
    onSuccess: () => {}
  })
}

export const useDeleteVideo = () => {
  const deleteVideo = useVideosStore(state => state.deleteVideo)

  return useMutation({
    mutationFn: (id: string) => deleteVideo(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteVideos = () => {
  const bulkDeleteVideos = useVideosStore(state => state.bulkDeleteVideos)
  const deselectAllVideos = useVideosStore(state => state.deselectAllVideos)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteVideos(ids),
    onSuccess: response => {
      deselectAllVideos()
      handleBulkDeleteSuccess(response)
    }
  })
}
